import { KeyValue } from '@angular/common';
import { currencyExp } from '../types/regular.expressions';
import {
  PropExtend,
  DeepSignal,
  WritableDeepSignal,
} from '../types/common.types';
import {
  Signal,
  WritableSignal,
  computed,
  isSignal,
  signal,
} from '@angular/core';

export namespace UTILS {
  export namespace CONSTANTS {
    export const EMPTY_ARRAY = [] as const;
    export const DAYS_TO_DDD = [
      'Sun',
      'Mon',
      'Tue',
      'Wed',
      'Thu',
      'Fri',
      'Sat',
    ];
    export const MONTHS_TO_MMM = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    export const NUMBERS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    export const DECIMALS = [...NUMBERS, '.'];
    export const SIGNED_NUMBERS = [...NUMBERS, '-'];
    export const SIGNED_DECIMALS = [...DECIMALS, '-'];
    export const NAVIGATION_KEYS = [
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab',
      'Enter',
    ];
    export const EDIT_KEYS = ['Backspace', 'Delete', 'F2', 'Escape'];
    export const SHORTCUT_NUMBER_MULTIPLIERS: Record<string, number> = {
      k: 1_000,
      K: 1_000,
      m: 1_000_000,
      M: 1_000_000,
      b: 1_000_000_000,
      B: 1_000_000_000,
      t: 1_000_000_000_000,
      T: 1_000_000_000_000,
    };
    export const SHORTCUT_NUMBER_KEYS = Object.keys(
      SHORTCUT_NUMBER_MULTIPLIERS,
    );
    export const SUBMIT_KEYS = ['Enter', 'Escape'];
    export const DEFAULT_ENUM_REPLACE_RULES: Array<KeyValue<RegExp, string>> = [
      { key: /([a-z])([A-Z])/g, value: '$1 $2' },
    ];
    export const VALID_MAP_TAGS = [
      'street_number',
      'premise',
      'route',
      'postal_code',
      'postal_town',
      'locality',
      'country',
      'administrative_area_level_1',
    ];
  }

  export namespace FORMAT {
    export function toAbbr(
      value: number,
      abbrSmall = false,
      decimalAmount = 3,
      thousandsSeparator = ',',
    ): string {
      let suffix = '';
      let abbr: number;

      const absValue = Math.abs(value);
      if (absValue < 1000000) {
        abbr = absValue;
      } else if (absValue < 1000000000) {
        abbr = absValue / 1000000;
        suffix = abbrSmall ? 'm' : ' million';
      } else if (absValue < 1000000000000) {
        abbr = absValue / 1000000000;
        suffix = abbrSmall ? 'b' : ' billion';
      } else {
        abbr = absValue / 1000000000000;
        suffix = abbrSmall ? 't' : ' trillion';
      }

      const final = Number.isInteger(abbr)
        ? parseInt(abbr.toString()).toString()
        : Number(abbr.toFixed(decimalAmount)).toString();
      return `${final.replace(currencyExp, thousandsSeparator)}${suffix}`;
    }

    export function toMonthMMM(value?: Date): string | undefined {
      if (value) {
        return CONSTANTS.MONTHS_TO_MMM[value.getMonth()];
      }
      return undefined;
    }

    export function toDateFormat(
      value: Date | string | undefined,
      format = 'mm/dd/yyyy',
      pad = false,
    ) {
      if (!value) return undefined;
      const date = typeof value === 'string' ? new Date(value) : value;
      const day = pad
        ? date.getDate().toString().padStart(2, '0')
        : date.getDate().toString();
      const month = pad
        ? (date.getMonth() + 1).toString().padStart(2, '0')
        : (date.getMonth() + 1).toString();
      return date instanceof Date
        ? format
            .replace('dd', day)
            .replace('mm', month)
            .replace('MMM', FORMAT.toMonthMMM(date)!)
            .replace('yyyy', date.getFullYear().toString())
        : undefined;
    }
  }

  export namespace DATE {
    //#region Conditionals

    export function isDate(value: unknown): boolean {
      return value instanceof Date && isFinite(value.getTime());
    }

    export function isValidDate(value: unknown): boolean {
      return (
        isDate(value) || new Date(value as string).toString() !== 'Invalid Date'
      );
    }

    export function isSameDate(
      a: Date | string | undefined,
      b: Date | string | undefined,
    ) {
      if (!a && !b) {
        return true;
      } else if (!a || !b) {
        return false;
      } else if (!isValidDate(a) || !isValidDate(b)) {
        return false;
      }
      const date1Value = toDate(a);
      const date2Value = toDate(b);
      return (
        date1Value?.getFullYear() === date2Value?.getFullYear() &&
        date1Value?.getMonth() === date2Value?.getMonth() &&
        date1Value?.getDate() === date2Value?.getDate()
      );
    }

    export function isSameDateCollection(
      a: Array<Date | string> | undefined,
      b: Array<Date | string> | undefined,
    ) {
      if (!a && !b) {
        return true;
      } else if (!a || !b || a.length !== b.length) {
        return false;
      } else {
        for (let i = 0; i < a.length; i++) {
          if (!isSameDate(a[i], b[i])) {
            return false;
          }
        }
        return true;
      }
    }

    export function isLeapYear(year: number): boolean {
      return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    }

    export function isEndOfMonth(value: Date | string | undefined) {
      if (!isDate(value)) {
        return false;
      }
      value = toDate(value);
      return (
        value!.getDate() ===
        DATE.getDaysInMonth(value!.getFullYear(), value!.getMonth())
      );
    }

    //#endregion

    //#region Date Query

    export function getDaysInMonth(year: number, month: number) {
      return [
        31,
        DATE.isLeapYear(year) ? 29 : 28,
        31,
        30,
        31,
        30,
        31,
        31,
        30,
        31,
        30,
        31,
      ][month];
    }

    export function getMonthDays(value: Date) {
      if (!isDate(value)) {
        return undefined;
      }
      return new Date(value.getFullYear(), value.getMonth() + 1, 0).getDate();
    }

    //#endregion

    //#region Date Conversion

    export function toLocalISOString(value: Date | string, zeroOffset = false) {
      if (!value) return undefined;
      if (typeof value === 'string') {
        value = new Date(value);
      }
      if (!isDate(value)) {
        return undefined;
      }

      return `${value.getFullYear()}-${(value.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${value
        .getDate()
        .toString()
        .padStart(2, '0')}T00:00:00.000${zeroOffset ? 'Z' : ''}`;
    }

    export function toUTCISOString(value: Date | string) {
      if (!value) return undefined;
      if (typeof value === 'string') {
        value = new Date(value);
      }
      if (!isDate(value)) {
        return undefined;
      }
      return value.toISOString();
    }

    export function fromUtc(value: Date | string | undefined | null) {
      if (!value) return undefined;
      if (typeof value !== 'string') return value;
      const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
      const date = new Date(hasOffset ? value : `${value}Z`);
      return isDate(date) ? date : undefined;
    }

    export function toDate(value: Date | string | undefined) {
      return !value
        ? undefined
        : typeof value === 'string'
          ? new Date(value)
          : value;
    }

    export function toLocalDate(value: Date | string | undefined) {
      if (!value) return undefined;
      const stringValue =
        typeof value === 'string' ? value : UTILS.DATE.toLocalISOString(value);
      const newDate = new Date(stringValue!);
      return isDate(newDate) ? newDate : undefined;
    }

    export function toLocalDateString(value: Date) {
      return value
        ? `${(value.getMonth() + 1).toString().padStart(2, '0')}/${value.getDate().toString().padStart(2, '0')}/${value.getFullYear()}`
        : '';
    }

    export function toEndOfMonth(value: Date) {
      if (!isDate(value)) {
        return undefined;
      }

      return new Date(value!.getFullYear(), value!.getMonth() + 1, 0);
    }

    //#endregion

    //#region Date Manipulation

    export function addYear(value: Date, years = 1, mutate = false) {
      if (!isDate(value)) {
        return undefined;
      }
      const newValue = mutate ? value : new Date(value);
      newValue.setFullYear(newValue.getFullYear() + years);
      return newValue;
    }

    export function addMonth(
      value: Date,
      months = 1,
      mutate = false,
      adjustEndOfMonth = false,
    ) {
      if (!isDate(value)) {
        return undefined;
      }
      let newValue = mutate ? value : new Date(value);
      if (adjustEndOfMonth && DATE.isEndOfMonth(value)) {
        const newMonth =
          newValue.getMonth() + months > 11
            ? newValue.getMonth() + months - 12
            : newValue.getMonth() + months;
        const newYear =
          newValue.getMonth() + months > 11
            ? newValue.getFullYear() + 1
            : newValue.getFullYear();
        newValue = DATE.toEndOfMonth(
          new Date(newYear, newMonth, 1, 0, 0, 0, 0),
        )!;
      } else {
        newValue.setMonth(newValue.getMonth() + months);
      }

      return newValue;
    }

    export function addDay(value: Date, days = 1, mutate = false) {
      if (!isDate(value)) {
        return undefined;
      }
      const newValue = mutate ? value : new Date(value);
      newValue.setDate(newValue.getDate() + days);
      return newValue;
    }

    //#endregion

    //#region Specific (TODO: Probably move them elsewhere)

    export function getStickyDate(
      stickyDateOption: number,
      publishDate: string,
    ) {
      if (!stickyDateOption) return undefined;

      const startDate = publishDate ? new Date(publishDate) : new Date();
      const returnDate = new Date(startDate);
      switch (stickyDateOption) {
        case ActivityStickyDates.OneDay:
          returnDate.setDate(startDate.getDate() + 1);
          break;
        case ActivityStickyDates.ThreeDays:
          returnDate.setDate(startDate.getDate() + 3);
          break;
        case ActivityStickyDates.OneWeek:
          returnDate.setDate(startDate.getDate() + 7);
          break;
        case ActivityStickyDates.TwoWeeks:
          returnDate.setDate(startDate.getDate() + 14);
          break;
        case ActivityStickyDates.OneMonth:
          returnDate.setMonth(startDate.getMonth() + 1);
          break;
        default:
          break;
      }
      return returnDate.toLocaleDateString('en-US');
    }

    export function getMilliseconds(stickyDateOption: number) {
      let timeInMilliseconds = 0;

      if (!stickyDateOption) return timeInMilliseconds;

      switch (stickyDateOption) {
        case ActivityStickyDates.OneDay:
          timeInMilliseconds = 1 * 24 * 60 * 60 * 1000;
          break;
        case ActivityStickyDates.ThreeDays:
          timeInMilliseconds = 3 * 24 * 60 * 60 * 1000;
          break;
        case ActivityStickyDates.OneWeek:
          timeInMilliseconds = 7 * 24 * 60 * 60 * 1000;
          break;
        case ActivityStickyDates.TwoWeeks:
          timeInMilliseconds = 14 * 24 * 60 * 60 * 1000;
          break;
        case ActivityStickyDates.OneMonth:
          timeInMilliseconds = 30 * 24 * 60 * 60 * 1000;
          break;
        default:
          break;
      }
      return timeInMilliseconds;
    }

    enum ActivityStickyDates {
      OneDay = 1,
      ThreeDays = 2,
      OneWeek = 3,
      TwoWeeks = 4,
      OneMonth = 5,
    }

    //#endregion
  }

  export namespace STRING {
    export function trimEnd(value: string, char: string) {
      while (value.at(-1) === char) {
        value = value.slice(0, -1);
      }
      return value;
    }

    export function removeTrailingUnderscores(name: string) {
      const nameArr = name.split('.');
      const ext = nameArr.pop();
      let nameWithoutExt = nameArr.join('.');
      let hasUnderscores = true;
      while (hasUnderscores) {
        if (nameWithoutExt.charAt(nameWithoutExt.length - 1) === '_') {
          nameWithoutExt = nameWithoutExt.slice(0, -1);
        } else hasUnderscores = false;
      }
      return `${nameWithoutExt}.${ext}`;
    }

    export function sanitizeName(name: string) {
      return name.replace(/[//?<>\\:*|,.&'"+@%#/{} /]+/g, '_');
    }

    export function isNullOrEmpty(text?: string) {
      return (
        text === undefined ||
        text === null ||
        !text ||
        text.length === 0 ||
        /^\s*$/.test(text)
      );
    }

    export function isValidLink(text: string) {
      // Regular expression to check if the input text is a valid URL
      const urlRegex =
        /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-/]))?/;
      return urlRegex.test(text);
    }

    export function isValidEmail(email: string) {
      return String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        );
    }

    export function multiReplace(
      key: string,
      replaceRules: Array<KeyValue<RegExp | string, string>>,
    ) {
      let keyAfterReplace: string = key;
      replaceRules.forEach((item) => {
        keyAfterReplace = keyAfterReplace.replace(item.key, item.value);
      });
      return keyAfterReplace;
    }

    export function findSubstringDifferences(
      str1: string,
      str2: string,
    ): Array<string> {
      const diff: string[] = [];
      const str1Parts = str1.split(' ');
      const str2Parts = str2.split(' ');
      str1Parts.forEach((part, index) => {
        if (part !== str2Parts[index]) {
          diff.push(`'${part}' vs '${str2Parts[index] || ''}'`);
        }
      });
      return diff;
    }

    export function initials(name: string | undefined, lowercase?: boolean) {
      let initials = '';
      if (!name) return initials;
      const nameArr = name.split(' ');
      if (nameArr.length === 1) {
        initials = nameArr[0].substring(0, 2).toUpperCase();
      } else {
        initials = nameArr.map((name) => name.charAt(0).toUpperCase()).join('');
      }

      if (lowercase) {
        initials = initials.toLowerCase();
      }
      return initials;
    }

    export function toCamelCase(
      value: string,
      lowerFirst = false,
    ): string | undefined {
      if (!value) return undefined;
      const words = value.split(' ');
      const camelCaseValue = words
        .map((word, index) => {
          if (index === 0 && lowerFirst) {
            return word.charAt(0).toLowerCase() + word.slice(1);
          }
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join('');
      return camelCaseValue;
    }

    export function capitalizeFirstLetter(value: string): string {
      if (!value) return '';
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  }

  export namespace UI {
    export function enumToOptions(
      enumSource: PropExtend<unknown>,
      replaceRules: Array<
        KeyValue<RegExp, string>
      > = CONSTANTS.DEFAULT_ENUM_REPLACE_RULES,
      omit: Array<string | number> = [],
    ): Array<{ label: string; value: number | string }> {
      const entries = Object.entries(enumSource).filter(
        (entry) => typeof entry.at(-1) !== 'function',
      );

      let keys = entries.map((entry) => entry.at(0));
      let values = entries.map((entry) => entry.at(-1));

      if (typeof entries.at(-1)?.at(-1) === 'number') {
        const halfEntries = entries.slice(entries.length / 2);
        values = halfEntries.map((entry) => entry.at(-1));
        keys = halfEntries.map((entry) => entry.at(0));
      }

      const valueLabels = keys.map((key, index) => ({
        label:
          typeof values[index] === 'number'
            ? replaceRules?.length
              ? STRING.multiReplace(key as string, replaceRules!)
              : (key as string)
            : replaceRules?.length
              ? STRING.multiReplace(values[index] as string, replaceRules!)
              : (values[index] as string),
        value: values[index] as number | string,
      }));

      if (omit.length > 0) {
        return valueLabels.filter((item) => !omit.includes(item.value));
      }

      return valueLabels;
    }

    export function enumToValueArray(
      enumSource: PropExtend<unknown>,
      omit: Array<string | number> = [],
    ): Array<string | number> {
      const entries = Object.entries(enumSource).filter(
        (entry) => typeof entry.at(-1) !== 'function',
      );

      let values = entries.map((entry) => entry.at(-1));

      if (typeof entries.at(-1)?.at(-1) === 'number') {
        const halfEntries = entries.slice(entries.length / 2);
        values = halfEntries.map((entry) => entry.at(-1));
      }

      const result = values as Array<string | number>;

      if (omit.length > 0) {
        return result.filter((v) => !omit.includes(v));
      }

      return result;
    }

    export function dataToOptions(
      dataSource:
        Array<PropExtend<unknown>> | Signal<Array<PropExtend<unknown>>>,
      labelField = 'label',
      valueField = 'value',
      deleteField = 'isDeleted',
    ): Array<{
      label: string;
      value: number | string;
      isDeleted: boolean;
      isSelectable: boolean;
      isSelected: boolean;
    }> {
      return (
        typeof dataSource === 'function' ? dataSource() : dataSource
      )?.map((item) => {
        item = typeof item === 'function' ? item() : item;
        const label =
          typeof item[labelField] === 'function'
            ? item[labelField]()
            : item[labelField];
        const value =
          typeof item[valueField] === 'function'
            ? item[valueField]()
            : item[valueField];
        const isDeleted =
          typeof item[deleteField] === 'function'
            ? item[deleteField]()
            : item[deleteField];
        const data = item;
        const isSelectable = true;
        const isSelected = false;
        return { label, value, isDeleted, data, isSelectable, isSelected };
      });
    }

    export function remToPixels(rem: string): number {
      const htmlFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );
      const remValue = parseFloat(rem.replace('rem', ''));
      if (isNaN(remValue)) {
        return 0;
      }
      return remValue * htmlFontSize;
    }

    export function userColorStyle(
      userId: number | string | null | undefined,
    ): Record<string, string> {
      if (
        userId === null ||
        userId === undefined ||
        userId === -1 ||
        userId === ''
      ) {
        return {
          '--user-bg': 'rgb(209 213 219)',
          '--user-bg-dark': 'rgb(55 65 81)',
        };
      }
      const str = String(userId);
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
      }
      const hue = hash % 360;
      return {
        '--user-bg': `hsl(${hue}, 65%, 75%)`,
        '--user-bg-dark': `hsl(${hue}, 50%, 35%)`,
      };
    }
  }

  export namespace CLASS {
    export function applyMixins(baseClass: any, extendedClasses: any[]) {
      extendedClasses.forEach((extendedClass) => {
        Object.getOwnPropertyNames(extendedClass.prototype).forEach((name) => {
          Object.defineProperty(
            baseClass.prototype,
            name,
            Object.getOwnPropertyDescriptor(extendedClass.prototype, name) ||
              Object.create(null),
          );
        });
      });
    }
  }

  export namespace OBJECT {
    export function isNil(value: unknown): boolean {
      return value === null || value === undefined;
    }

    export function isObject(value: unknown): boolean {
      return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        !(value instanceof RegExp) &&
        !(value instanceof Date) &&
        !(value instanceof Set) &&
        !(value instanceof Map)
      );
    }

    export function isEmpty(value: unknown): boolean {
      return isNil(value) || value === '';
    }

    export function toFiniteNumber(value: unknown): number | undefined {
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
      }
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
      }
      return undefined;
    }

    export function areEqual(a: unknown, b: unknown) {
      return (
        (isNil(a) && isNil(b)) ||
        (a instanceof Date && b instanceof Date
          ? DATE.toLocalISOString(a! as Date) ===
            DATE.toLocalISOString(b! as Date)
          : a === b)
      );
    }

    export function areDeepEqual(
      a: PropExtend<unknown>,
      b: PropExtend<unknown>,
      log = false,
    ) {
      const falseWithLog = (a: any, b: any, prop?: string) => {
        if (log) {
          console.warn(prop);
          console.warn('a: ', a);
          console.warn('b: ', b);
        }
        return false;
      };
      if (a === b) return true;
      if (a instanceof Date && b instanceof Date) {
        return DATE.toLocalISOString(a) === DATE.toLocalISOString(b);
      }
      if (typeof a === 'function' || typeof b === 'function') return true;
      if (typeof a !== 'object' || typeof b !== 'object')
        return falseWithLog(a, b, 'No Object');
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length)
          return falseWithLog(a, b, 'No Equal Array Length');
        for (let i = 0; i < a.length; i++) {
          const hasEqual = b.find((item) => areDeepEqual(a[i], item));
          if (!hasEqual) return falseWithLog(a, b, 'No Equal Array Item');
        }
        return true;
      }
      if (UTILS.OBJECT.isNil(a) || UTILS.OBJECT.isNil(b))
        return falseWithLog(a, b, 'At least one Object Is Nill');
      if (Object.keys(a).length !== Object.keys(b).length)
        return falseWithLog(a, b, 'No Equal Object Length');

      for (const prop in a) {
        if (!Object.hasOwn(b, prop))
          return falseWithLog(a, b, `Object does not own property: ${prop}`);
        if (!areDeepEqual(a[prop], b[prop]))
          return falseWithLog(
            a[prop],
            b[prop],
            `No Equal Object property: ${prop}`,
          );
      }

      return true;
    }

    export function assign(
      target: PropExtend<unknown>,
      source: PropExtend<unknown>,
    ) {
      for (const prop in source) {
        if (Object.hasOwn(source, prop)) {
          if (Array.isArray(source[prop])) {
            target[prop] = [...source[prop]];
          } else if (
            typeof source[prop] === 'object' &&
            source[prop] !== null
          ) {
            target[prop] = Object.assign(target[prop] || {}, source[prop]);
          } else {
            target[prop] = source[prop];
          }
        }
      }
      return target;
    }

    export function assignByJson(source: unknown, omit?: Array<string>) {
      return JSON.parse(
        JSON.stringify(source, function (key, value) {
          return !omit?.includes(key) ? value : undefined;
        }),
      );
    }

    export function assignToPath(
      target: PropExtend<unknown>,
      fieldPath: string,
      value: unknown,
    ) {
      if (!fieldPath.includes('.')) {
        target[fieldPath] = value;
      } else {
        const childField = fieldPath.split('.')[0];
        const remainingFields = fieldPath.split('.').slice(1).join('.');
        if (!Object.hasOwn(target, childField)) {
          target[childField] = {};
        }
        assignToPath(target[childField], remainingFields, value);
      }
      return target;
    }

    /**
     * #findSemanticDiffs is a function that compares two objects and identifies semantic differences between them. by semantic differences, we mean changes that are meaningful in the context of the data being compared, rather than just superficial changes like property order or formatting.
     * @param a
     * @param b
     */
    export function findSemanticDiffs(
      a: PropExtend<unknown>,
      b: PropExtend<unknown>,
    ) {
      const diffs = new Map<string, { a: unknown; b: unknown }>();

      const recordDiff = (path: string, va: unknown, vb: unknown) => {
        const key = path || '$root';
        if (!diffs.has(key)) {
          diffs.set(key, { a: va, b: vb });
        }
      };

      const walk = (va: unknown, vb: unknown, path: string) => {
        if (isNil(va) && isNil(vb)) {
          return;
        }

        if (va instanceof Date && vb instanceof Date) {
          if (DATE.toLocalISOString(va) !== DATE.toLocalISOString(vb)) {
            recordDiff(path, va, vb);
          }
          return;
        }

        if (typeof va === 'function' || typeof vb === 'function') {
          return;
        }

        if (Array.isArray(va) || Array.isArray(vb)) {
          if (!Array.isArray(va) || !Array.isArray(vb)) {
            recordDiff(path, va, vb);
            return;
          }

          if (va.length !== vb.length) {
            recordDiff(path, va, vb);
          }

          const maxLength = Math.max(va.length, vb.length);
          for (let i = 0; i < maxLength; i++) {
            const nextPath = `${path}[${i}]`;
            walk(va[i], vb[i], nextPath);
          }
          return;
        }

        if (isObject(va) || isObject(vb)) {
          if (!isObject(va) || !isObject(vb)) {
            recordDiff(path, va, vb);
            return;
          }

          const keys = new Set<string>([
            ...Object.keys(va as object),
            ...Object.keys(vb as object),
          ]);

          for (const key of keys) {
            const hasA = Object.hasOwn(va as object, key);
            const hasB = Object.hasOwn(vb as object, key);
            const nextPath = path ? `${path}.${key}` : key;

            if (!hasA) {
              recordDiff(nextPath, undefined, (vb as any)[key]);
              continue;
            }

            if (!hasB) {
              recordDiff(nextPath, (va as any)[key], undefined);
              continue;
            }

            walk((va as any)[key], (vb as any)[key], nextPath);
          }
          return;
        }

        if (va !== vb) {
          recordDiff(path, va, vb);
        }
      };

      walk(a, b, '');
      return diffs;
    }
  }

  export namespace ARRAY {
    export function compareArrayByJSON(
      a: Array<unknown>,
      b: Array<unknown>,
      treatEmptyAsNil = false,
    ) {
      let aValue, bValue;
      if (treatEmptyAsNil) {
        aValue = a?.length === 0 ? undefined : a;
        bValue = b?.length === 0 ? undefined : b;
      } else {
        aValue = a;
        bValue = b;
      }

      return (
        (!aValue && !bValue) ||
        JSON.stringify(aValue) === JSON.stringify(bValue)
      );
    }

    export function customSort(collection: any[], sorts: any[]) {
      sorts.map(
        (sort: { uniques: any[]; key: string | number; inverse: any }) => {
          sort.uniques = Array.from(
            new Set(
              collection.map((obj: { [x: string]: any }) => obj[sort.key]),
            ),
          );

          sort.uniques = sort.uniques.sort((a, b) => {
            if (typeof a == 'string') {
              return sort.inverse ? b.localeCompare(a) : a.localeCompare(b);
            } else if (typeof a == 'number') {
              return sort.inverse ? b - a : a - b;
            } else if (typeof a == 'boolean') {
              const x = sort.inverse
                ? a === b
                  ? 0
                  : a
                    ? -1
                    : 1
                : a === b
                  ? 0
                  : a
                    ? 1
                    : -1;
              return x;
            }
            return 0;
          });
        },
      );

      const weightOfObject = (obj: { [x: string]: any }) => {
        let weight = '';
        sorts.map(
          (sort: {
            uniques: {
              length: any;
              indexOf: (arg0: any) => {
                (): any;
                new (): any;
                toString: { (): string; new (): any };
              };
            };
            key: string | number;
          }) => {
            const zeropad = `${sort.uniques.length}`.length;
            weight += sort.uniques
              .indexOf(obj[sort.key])
              .toString()
              .padStart(zeropad, '0');
          },
        );
        //obj.weight = weight; // if you need to see weights
        return weight;
      };

      collection.sort((a: { [x: string]: any }, b: { [x: string]: any }) => {
        return weightOfObject(a).localeCompare(weightOfObject(b));
      });

      return collection;
    }

    export function distinct<T>(collection: Array<T>) {
      return collection?.length > 0 ? [...new Set(collection)] : [];
    }
  }

  export class GENERATE {
    //#region UID

    static uid = 1;
    static get nextuid() {
      return this.uid++;
    }

    //#endregion
  }

  export namespace GENERATE {
    //TODO: This function is very specific, it should be moved
    export function generateDownloadUrl(
      name: string,
      projectId: number,
      dataDefId: number,
    ) {
      const nameArr = name.split('.');
      const extension = nameArr.pop();
      let nameWithoutExt = UTILS.STRING.sanitizeName(nameArr.join());
      if (nameWithoutExt.charAt(nameWithoutExt.length - 1) === '_') {
        nameWithoutExt = nameWithoutExt.slice(0, -1);
      }
      return `/ProjectDocumentManager/GetDocument?projectId=${projectId}&dataDefId=${dataDefId}&fileName=${nameWithoutExt}&ext=${extension}`;
    }

    //TODO: This function is very specific, it should be moved
    export function generateAIDownloadUrl(
      name: string,
      projectId: number,
      dataDefId: number,
      startPage?: number,
      endPage?: number,
    ) {
      const nameArr = name.split('.');
      const extension = nameArr.pop();
      let nameWithoutExt = STRING.sanitizeName(nameArr.join());
      if (nameWithoutExt.charAt(nameWithoutExt.length - 1) === '_') {
        nameWithoutExt = nameWithoutExt.slice(0, -1);
      }

      return `/ProjectDocumentManager/GetExcelFromAIWithScheduleOfInvestments?projectId=${projectId}&dataDefinitionId=${dataDefId}&fileName=${nameWithoutExt}&ext=${extension}&scheduleOfInvestmentsStartingPage=${startPage}&scheduleOfInvestmentsEndingPage=${endPage}`;
    }
  }

  export namespace EVENTS {
    export async function copyToClipboard(value: string): Promise<boolean> {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(value);
        return true;
      }
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');

      document.body.removeChild(textArea);
      return successful;
    }
  }

  export namespace KEYVALUE {
    export function enumToKeyValue(
      enumProp: any,
      replaceRules?: Array<KeyValue<RegExp, string>>,
    ): Array<KeyValue<number | string, string>> {
      const entries = Object.entries(enumProp).filter(
        (entry) => typeof entry.at(-1) !== 'function',
      );

      let keys = entries.map((entry) => entry.at(0));
      let values = entries.map((entry) => entry.at(-1));

      if (typeof entries.at(-1)?.at(-1) === 'number') {
        const halfEntries = entries.slice(entries.length / 2);
        keys = halfEntries.map((entry) => entry.at(-1));
        values = halfEntries.map((entry) => entry.at(0));
      }

      const keyValues: Array<KeyValue<number | string, string>> = keys.map(
        (key, index) => ({
          value:
            (replaceRules?.length ?? 0) > 0
              ? STRING.multiReplace(values[index] as string, replaceRules!)
              : (values[index] as string),
          key: key as number | string,
        }),
      );

      return keyValues;
    }

    export function toKeyValue<
      T extends { [s: string]: unknown } | ArrayLike<unknown>,
    >(this: T) {
      return Object.entries(this)
        .filter((prop) => typeof prop.at(-1) !== 'function')
        .map((view) => ({
          key: view.at(0) as string,
          value: view.at(1) as string,
        }));
    }

    export function getKeyFromValue<
      T extends { [s: string]: unknown } | ArrayLike<unknown>,
    >(this: T, value: string) {
      return Object.entries(this)
        .filter((prop) => typeof prop.at(-1) !== 'function')
        ?.map((view) => ({ key: view.at(0), value: view.at(1) }))
        ?.find((item) => item.value === value)?.key;
    }

    export function splitCamelCase(value: string) {
      return value.replace(/([a-z])([A-Z])/g, '$1 $2');
    }

    export function isNullOrEmpty(text: any) {
      return (
        text === undefined ||
        text === null ||
        !text ||
        text.length === 0 ||
        /^\s*$/.test(text)
      );
    }
  }

  export namespace SIGNALS {
    export function isDeepSignal(
      value: PropExtend<unknown>,
    ): value is DeepSignal<unknown> {
      if (isSignal(value)) {
        return isDeepSignal(value());
      } else if (Array.isArray(value)) {
        return isSignal(value?.at(0));
      } else if (OBJECT.isObject(value)) {
        for (const prop in value) {
          if (!isSignal(value[prop])) {
            return false;
          }
        }
        return true;
      }
      return false;
    }
    export function toDeepSignalArray<T>(
      plain: Array<T>,
      instance: { new (): PropExtend<T> } = class {} as any,
    ): WritableDeepSignal<Array<T>> {
      return signal(
        plain.map((item: T) =>
          typeof item === 'object'
            ? toDeepSignal<T>(item, instance)
            : (signal(item) as WritableSignal<DeepSignal<T> | undefined>),
        ) as DeepSignal<Array<T>> | undefined,
      );
    }
    export function toDeepSignal<T>(
      plain: T,
      instance: { new (): PropExtend<T> } = class {} as any,
    ): WritableDeepSignal<T> {
      if (!plain) return signal(undefined);
      else if (Array.isArray(plain)) {
        return toDeepSignalArray(plain, instance) as WritableSignal<
          DeepSignal<T> | undefined
        >;
      } else {
        const unassignedInstance = new instance();
        const newInstance = Object.assign(new instance(), plain);
        const signaled: PropExtend<T> = {};
        signaled._plain = computed(() => UTILS.SIGNALS.toPlain(signaled));
        signaled._deepThis = signal(signaled);
        for (const prop in newInstance) {
          if (typeof unassignedInstance[prop] === 'function') {
            const unassignedFn = unassignedInstance[prop] as Function;
            signaled[prop] = (value: unknown) =>
              unassignedFn.call(undefined, value, signaled._deepThis);
          } else if (Array.isArray(newInstance[prop])) {
            signaled[prop] = signal(
              (newInstance[prop] as Array<unknown>)?.map(
                (item: PropExtend<unknown>) => {
                  const itemArray: typeof item & {
                    new (): PropExtend<unknown>;
                  } =
                    unassignedInstance[prop]?.at?.(0)?.constructor ??
                    (class {} as any);
                  return typeof item === 'object'
                    ? toDeepSignal<typeof itemArray>(item, itemArray)
                    : signal(item);
                },
              ),
            );
          } else if (UTILS.DATE.isDate(newInstance[prop])) {
            signaled[prop] = signal(newInstance[prop]);
          } else if (OBJECT.isObject(newInstance[prop])) {
            const itemInstance: (typeof newInstance)[typeof prop] & {
              new (): PropExtend<unknown>;
            } = unassignedInstance[prop]?.constructor ?? (class {} as any);
            signaled[prop] = toDeepSignal(newInstance[prop], itemInstance);
          } else {
            signaled[prop] = signal(newInstance[prop]);
          }
        }
        return signal(signaled);
      }
    }

    export function toPlainArray<T>(
      deepSignal: DeepSignal<T> | undefined,
    ): T | undefined {
      return Object.entries(toPlain(deepSignal)!).map(([_, val]) => val) as T;
    }

    export function toPlain<T>(
      deepSignal: DeepSignal<T> | undefined,
    ): T | undefined {
      if (!deepSignal) return undefined;
      else if (Array.isArray(deepSignal)) {
        const plain = deepSignal.map((item) => toPlain(item())) as T;
        return plain;
      } else {
        const plain = {} as PropExtend<T>;
        for (const prop in deepSignal) {
          if (prop.startsWith('_')) continue;
          else if (Array.isArray(deepSignal![prop]())) {
            plain[prop] = (
              deepSignal![prop]() as Array<WritableSignal<DeepSignal<unknown>>>
            )?.map((item) => {
              return typeof item() === 'object' ? toPlain(item()) : item();
            });
          } else if (UTILS.DATE.isDate(deepSignal![prop])) {
            plain[prop] = UTILS.DATE.toLocalISOString(
              deepSignal![prop]() as Date,
            );
          } else if (typeof deepSignal![prop]() === 'object') {
            plain[prop] = toPlain(deepSignal![prop]());
          } else {
            plain[prop] = deepSignal![prop]();
          }
        }
        return plain;
      }
    }
  }

  export class LOGS {
    static GROUP_LOGS: Map<
      string,
      { content: Array<any>; color: string; background: string }
    > = new Map();
  }

  export namespace LOGS {
    export function log(arg: any, color = 'white', background = 'transparent') {
      console.log(`%c ${arg}`, `background: ${background}; color: ${color};`);
    }

    export function groupStart(
      key: string,
      content?: any,
      color?: string,
      background?: string,
    ) {
      color = color || 'white';
      background = background || 'transparent';
      LOGS.GROUP_LOGS.set(key, { content: [content], color, background });
    }

    export function groupLog(
      key: string,
      content: any,
      color?: string,
      background?: string,
    ) {
      color = color || 'white';
      background = background || 'transparent';
      const groupLog = LOGS.GROUP_LOGS.get(key);
      if (groupLog) {
        groupLog.content.push(content);
        LOGS.GROUP_LOGS.set(key, groupLog);
      } else {
        LOGS.GROUP_LOGS.set(key, { content: [content], color, background });
      }
    }

    export function groupEnd(key: string) {
      const groupLog = LOGS.GROUP_LOGS.get(key);
      if (!groupLog) {
        console.warn(`Group with key "${key}" does not exist.`);
        return;
      }
      console.groupCollapsed(
        `%c${key}`,
        `background: ${groupLog.background}; color: ${groupLog.color};`,
      );
      console.log(
        `%c{`,
        `background: ${groupLog.background}; color: ${groupLog.color};`,
      );
      groupLog.content.forEach((logContent) => {
        console.log(logContent);
      });
      console.log(
        `%c}`,
        `background: ${groupLog.background}; color: ${groupLog.color};`,
      );
      console.groupEnd();
      LOGS.GROUP_LOGS.delete(key);
    }
  }

  export namespace QUANT {
    export function calculateYtdCompounds(
      datapoints: Array<{ asOf: string; value: number }>,
    ) {
      if (!datapoints || datapoints.length === 0) {
        return [];
      }
      const ytdCollection: Array<{ asOf: string; value: number }> = [];

      datapoints.sort((a, b) => {
        return new Date(a.asOf).getTime() - new Date(b.asOf).getTime();
      });

      const groupedByYear = Object.groupBy(datapoints!, (point) =>
        new Date(point.asOf as unknown as string)!.getFullYear(),
      );
      Object.entries(groupedByYear).forEach(([year, points]) => {
        let ytd = 100.0;
        points?.forEach((point, index) => {
          ytd += (point.value || 0) * ytd;
        });
        ytd = (ytd - 100.0) / 100.0;
        ytdCollection.push({
          asOf: `${year}-12-31T00:00:00.000`,
          value: ytd,
        });
      });
      return ytdCollection;
    }
  }

  export namespace FILE {
    export function downloadFile(
      fileName: string,
      content: string,
      contentType: string,
    ) {
      const blob = new Blob([content], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }

    export function filenameFromContentDisposition(
      disposition: string | null | undefined,
      fallback: string,
    ): string {
      const header = disposition ?? '';
      const extended = /filename\*\s*=\s*[^';]*'[^';]*'([^;]+)/i.exec(header);
      const decoded = extended
        ? decodePercentEncoded(extended[1].trim())
        : undefined;
      if (decoded) return decoded;
      const plain = /filename(?!\*)\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header);
      const name = (plain?.[1] ?? plain?.[2])?.trim();
      return name || fallback;
    }

    function decodePercentEncoded(value: string): string | undefined {
      try {
        return decodeURIComponent(value);
      } catch {
        return undefined;
      }
    }
  }
}
